import { useState, useRef } from 'react'

/**
 * variant: 'toggle' (default) — red/green based on isActive
 *          'push'             — neutral amber, no active state indicator
 *
 * copyUrl: when provided, Shift+click copies the URL to clipboard instead of
 *          calling onClick. A brief "Copied!" flash confirms the action.
 */
function ToggleDashboardButton({
	label,
	icon,
	isActive,
	onClick,
	copyUrl,
	size = 80,
	variant = 'toggle',
}) {
	const active = Boolean(isActive)
	const isPush = variant === 'push'

	const [copied, setCopied] = useState(false)
	const copiedTimer = useRef(null)

	function handleClick(e) {
		if (e.shiftKey && copyUrl) {
			navigator.clipboard.writeText(copyUrl).catch(() => {})
			setCopied(true)
			clearTimeout(copiedTimer.current)
			copiedTimer.current = setTimeout(() => setCopied(false), 1200)
			return
		}
		onClick?.(e)
	}

	const buttonClass = [
		'relative flex flex-col items-center overflow-hidden rounded-md',
		'cursor-pointer select-none transition-all duration-150 p-1.5',
		'border-[3px] outline-none focus:outline-none',
		'active:scale-95 active:brightness-75',
		isPush
			? 'bg-[#3a2e10] border-amber-500 hover:bg-[#4a3a14] hover:brightness-110'
			: active
				? 'bg-[#1a4a1a] border-green-500 hover:bg-[#225a22] hover:brightness-110'
				: 'bg-[#4a1a1a] border-red-500 hover:bg-[#5a2222] hover:brightness-110',
		icon ? 'justify-between' : 'justify-center',
	].join(' ')

	const dotClass = [
		'absolute top-1 right-1 w-1.5 h-1.5 rounded-full',
		isPush ? 'bg-amber-500' : active ? 'bg-green-500' : 'bg-red-500',
	].join(' ')

	const labelClass = [
		'text-white text-[10px] font-bold font-mono text-center',
		'leading-tight break-words shrink-0 max-w-full',
		icon ? 'pt-0.5' : '',
	].join(' ')

	return (
		<button
			className={buttonClass}
			style={{ width: size, height: size }}
			onClick={handleClick}
			title={copyUrl ? `${label}\nShift+click to copy URL` : label}
		>
			<span className={dotClass} />
			{icon && (
				<img
					src={icon}
					alt={label}
					className="flex-1 w-full object-contain min-h-0"
					draggable={false}
				/>
			)}
			{label && (
				<span
					className={labelClass}
					style={{ textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}
				>
					{label}
				</span>
			)}

			{copied && (
				<div className="absolute inset-0 flex items-center justify-center bg-black/70 rounded-md">
					<span className="text-white text-[9px] font-bold font-mono">
						Copied!
					</span>
				</div>
			)}
		</button>
	)
}

export default ToggleDashboardButton
